import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<number[]>([0]); // First section open by default

  const toggleSection = (index: number) => {
    setExpandedSections(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  if (!isOpen) return null;

  const sections = t('footer.modals.privacy.sections', { returnObjects: true }) as any[];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Blurred background */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal content */}
      <div className="relative bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-4">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {t('footer.modals.privacy.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-slate-700/50 hover:bg-slate-600/50 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-all duration-200"
          >
            ×
          </button>
        </div>
        
        {/* Scrollable content with collapsible sections */}
        <div className="p-6 overflow-y-auto max-h-[75vh]">
          <div className="text-center mb-6">
            <p className="text-slate-300 text-sm">
              {t('footer.modals.privacy.description')}
            </p>
          </div>

          <div className="space-y-3">
            {sections.map((section: any, index: number) => (
              <div key={index} className="bg-slate-700/30 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-slate-600/30 transition-colors duration-200"
                >
                  <h3 className="text-lg font-semibold text-white">
                    {section.title}
                  </h3>
                  {expandedSections.includes(index) ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.includes(index) && (
                  <div className="px-4 pb-4">
                    <div className="border-t border-slate-600/30 pt-4">
                      <div className="text-slate-300 space-y-3 text-sm">
                        {section.content && (
                          <div dangerouslySetInnerHTML={{ __html: section.content }} />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
